import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TypographyComponent } from '@/app/components/ui/typography.component';
import { TableComponent } from '@/app/components/ui/table/table.component';
import { NotificationService } from '@/app/services/common/notification.service';
import { AttendanceService } from '@/app/services/common/attendance.service';
import { AttendanceData, AttendanceFilter } from '@/types/attendance.interfaces';
import { HttpErrorResponse } from '@angular/common/http';
import { DashboardStatsComponent } from '@/app/components/ui/cards/dashboard/dashboard-stats.component';
import { DialogModule } from 'primeng/dialog';
import { SortEvent } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';
import { TableColumn } from '@/types/table.interface';
type SortOrder = 'ASC' | 'DESC';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    TypographyComponent,
    TableComponent,
    DashboardStatsComponent,
    DialogModule,
  ],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit {

  private notificationService = inject(NotificationService);
  private attendanceService = inject(AttendanceService);

  isLoading = false;
  totalRecords = 0;
  rowsPerPage = 6;

  sortField: string | null = null;
  sortOrder: SortOrder | null = null;

  attendance = signal<AttendanceData[]>([]);
  displayPreview = signal(false);
  previewUrl = signal('');

  columns: TableColumn<AttendanceData>[] = [
    { field: 'employee', header: 'Name', sortable: true },
    { field: 'date', header: 'Date', sortable: true, type: 'date' },
    { field: 'clockIn', header: 'Clock In', sortable: true },
    { field: 'manualEvidenceInUrl', header: 'Photo' },
    { field: 'status', header: 'Status', sortable: true },
  ];

  activeFilters = {
    searchTerm: '',
    status: '',
    startDate: '',
    endDate: '',
  };

  ngOnInit(): void {
    this.refreshTableData();
  }

 refreshTableData(event?: TableLazyLoadEvent): void {
    this.isLoading = true;

    const size: number = event?.rows ?? this.rowsPerPage;
    const first: number = event?.first ?? 0;
    const page: number = Math.floor(first / size) + 1;
    this.rowsPerPage = size;

    let apiSortBy: string | undefined = undefined;
    let apiSortOrder: SortOrder | undefined = undefined;

    if (event?.sortField) {
      const rawSortField = Array.isArray(event.sortField) ? event.sortField[0] : event.sortField;
      if (rawSortField === 'employee') {
        apiSortBy = 'name';
      } else {
        apiSortBy = rawSortField;
      }
      if (event.sortOrder === 1) {
        apiSortOrder = 'ASC';
      } else if (event.sortOrder === -1) {
        apiSortOrder = 'DESC';
      }
    }

    const params: AttendanceFilter = {
      page: page,
      size: size,
      search: this.activeFilters.searchTerm || undefined,
      status: this.activeFilters.status || undefined,
      startDate: this.activeFilters.startDate || undefined,
      endDate: this.activeFilters.endDate || undefined,
      sortBy: apiSortBy,
      sortOrder: apiSortOrder
    };

    this.attendanceService.getAllAttendance(params).subscribe({
      next: (res) => {
        this.attendance.set(res.data || []);
        this.totalRecords = res.metaData?.totalData ?? 0;
        this.isLoading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.notificationService.handleHttpError(err);
        this.isLoading = false;
      },
    });
  }

 onSortChange(event: SortEvent): void {
    if (!event.field || !event.order) return;

    this.sortField = event.field;
    this.sortOrder = event.order === 1 ? 'ASC' : 'DESC';

    this.refreshTableData({
      first: 0,
      rows: this.rowsPerPage,
    });
  }

  showPreview(url: string): void {
    this.previewUrl.set(url);
    this.displayPreview.set(true);
  }

  getInitials(name?: string): string {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((n) => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }

  getRandomBg(name?: string): string {
    const colors = ['#f87171', '#fbbf24', '#34d399', '#60a5fa', '#818cf8', '#a78bfa'];
    const index = (name?.length || 0) % colors.length;
    return colors[index];
  }
}
